import * as React from "react";
import { GetSnapshotEventGroupedResponseItem } from "@/modules/snapshots/businessReports.types";
import clsx from "clsx";
import { STATUS_LABEL_MAP } from "@/modules/snapshots/snapshotEvents.consts";
import { DirectoryUser } from "@/types/user";
import { useDateFormatter } from "@/hooks/useDateFormatter";

interface EventTooltipProps {
  hoveredEvent: GetSnapshotEventGroupedResponseItem;
  tooltipPosition: { x: number; y: number };
  users: Array<DirectoryUser>;
}

export const EventTooltip: React.FC<EventTooltipProps> = ({ hoveredEvent, tooltipPosition, users }) => {
  const user = users.find(({ id }) => id === hoveredEvent.creator);
  const { formatDateShort } = useDateFormatter();

  if (!hoveredEvent) {
    return null;
  }
  return (
    <div
      className="absolute z-[999] min-w-[280px] rounded-lg border border-border bg-white p-4 text-sm shadow-lg"
      style={{
        left: tooltipPosition.x,
        top: tooltipPosition.y,
        pointerEvents: "none",
      }}
    >
      <div className="mb-3 border-b border-border pb-2 text-[15px] font-semibold text-gray-500">Event Details</div>
      <div className="grid gap-2">
        <div className="flex flex-col gap-0.5">
          <div className="text-sm font-medium text-gray-500">Document:</div>
          <div className="text-xs text-secondary-foreground">{hoveredEvent.document_name}</div>
        </div>
        <div>
          <span className="text-sm font-medium text-gray-500">Date:</span>
          <span className="ml-2 text-secondary-foreground">{formatDateShort(hoveredEvent.event_date)}</span>
        </div>
        <div>
          <span className="text-sm font-medium text-gray-500">Collection:</span>
          <span className="ml-2 text-secondary-foreground">{hoveredEvent.collection}</span>
        </div>
        <div>
          <span className="text-sm font-medium text-gray-500">Creator:</span>
          <span className="ml-2 text-secondary-foreground">
            {user ? `${user.name} ${user.surname}` : hoveredEvent.creator}
          </span>
        </div>
        <div>
          <span className="text-sm font-medium text-gray-500">Action Takers:</span>
          <span className="ml-2 text-secondary-foreground">{hoveredEvent.action_takers.join(", ")}</span>
        </div>
        <div>
          <span className="text-sm font-medium text-gray-500">Event Type:</span>
          <span className="ml-2 text-secondary-foreground">{hoveredEvent.event_type}</span>
        </div>

        <div className="mt-1 flex items-center gap-2">
          <span className="text-sm font-medium text-gray-500">Status:</span>
          <span
            className={clsx("rounded-full px-2 py-0.5 text-[11px] font-medium text-white", {
              "bg-success": hoveredEvent.status === "done",
              "bg-info": hoveredEvent.status === "in_progress",
              "bg-danger": hoveredEvent.is_overdue,
            })}
          >
            {STATUS_LABEL_MAP[hoveredEvent.status]}
          </span>
        </div>
      </div>
    </div>
  );
};
